import { all, call, fork, put, take } from "redux-saga/effects";
import { NotificationCustomer } from "../../config/view_const/notification";
import { AppNotificationType } from "../../domain/model/app_notification_message";
import { Company } from "../../domain/model/company";
import { Project } from "../../domain/model/project";
import { ICompanyRepository } from "../../domain/repository/company_repository";
import { IEntryRepository } from "../../domain/repository/entry_repository";
import { IProjectRepository } from "../../domain/repository/project_repository";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { createCompanyAPI } from "../../infra/api/service/company_api";
import { createEntryAPI } from "../../infra/api/service/entry_api";
import { createProjectAPI } from "../../infra/api/service/project_api";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {
  createCustomerProjectActionCreator,
  CustomerProjectActionType,
  ICustomerProjectActionCreator,
  IRequestCreateEntryAction,
  IRequestDeleteEntryAction,
  IRequestGetProjectAction,
} from "../action/customer_project_action";
import { AddAppNotificationRequest } from "../request/app_notification_message_request";
import {
  GetEntryProjectsByCustomerResponse,
  GetProjectByCustomerResponse,
} from "../response/project_response";
import { handleErrorForHandler } from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const entryRepository: IEntryRepository = createEntryAPI(apiClient);
const actionCreator: ICustomerProjectActionCreator =
  createCustomerProjectActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
  createAppNotificationMessageActionCreator();

function* handleGetProjectInCustomerProject() {
  while (true) {
    try {
      // ToDo: 2019/01/03 Bookmark関係で500エラーが発生したため、いったん機能を外す
      const action: IRequestGetProjectAction = yield take(
        CustomerProjectActionType.REQUEST_GET_PROJECT
      );
      const project: Project = yield call(getProject, action.item.projectId);
      let company: Company;
      let entryProjects: Project[];
      [company, entryProjects] = yield all([
        call(getMaskCompany, project.companyID, project.id),
        call(getEntryProjectList),
      ]);
      const res: GetProjectByCustomerResponse =
        new GetProjectByCustomerResponse();
      res.project = project;
      res.company = company;
      res.entryProjects = entryProjects;
      yield put(actionCreator.callbackGetProjectAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackGetProjectAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleGetEntryProjectsInCustomerProject() {
  while (true) {
    try {
      yield take(CustomerProjectActionType.REQUEST_GET_ENTRY_PROJECTS);
      const entryProjects: Project[] = yield call(getEntryProjectList);
      const res: GetEntryProjectsByCustomerResponse =
        new GetEntryProjectsByCustomerResponse();
      res.entryProjects = entryProjects;
      yield put(actionCreator.callbackGetEntryProjectsAction(true, res));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackGetEntryProjectsAction(
          false,
          undefined,
          error.toString()
        )
      );
    }
  }
}

function* handleCreateEntryInCustomerProject() {
  while (true) {
    try {
      const action: IRequestCreateEntryAction = yield take(
        CustomerProjectActionType.REQUEST_CREATE_ENTRY
      );
      yield call(createEntry, action.item.projectId);
      yield put(actionCreator.callbackCreateEntryAction(true));
      yield put(actionCreator.requestGetEntryProjectsAction());

      const notificationReq: AddAppNotificationRequest =
        new AddAppNotificationRequest();
      notificationReq.type = AppNotificationType.INFO;
      notificationReq.message = NotificationCustomer.successCreateEntry;
      yield put(actionCreatorForNotification.requestAddAction(notificationReq));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackCreateEntryAction(false, error.toString())
      );
    }
  }
}

function* handleDeleteEntryInCustomerProject() {
  while (true) {
    try {
      const action: IRequestDeleteEntryAction = yield take(
        CustomerProjectActionType.REQUEST_DELETE_ENTRY
      );
      yield call(deleteEntry, action.item.projectId);
      yield put(actionCreator.callbackDeleteEntryAction(true));
    } catch (error) {
      yield fork(handleErrorForHandler, error);
      yield put(
        actionCreator.callbackDeleteEntryAction(false, error.toString())
      );
    }
  }
}

const getProject = (projectId: string): Promise<Project> => {
  return projectRepository.get(projectId);
};

const getEntryProjectList = (): Promise<Project[]> => {
  return projectRepository.getAllByEntry(0, 1000);
};

const getMaskCompany = (
  companyId: string,
  projectId: string
): Promise<Company> => {
  return companyRepository.getWithMask(companyId, projectId);
};

const createEntry = (projectId: string) => {
  return entryRepository.create(projectId);
};

const deleteEntry = (projectId: string) => {
  return entryRepository.delete(projectId);
};

export {
  handleGetProjectInCustomerProject,
  handleCreateEntryInCustomerProject,
  handleDeleteEntryInCustomerProject,
  handleGetEntryProjectsInCustomerProject,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_project_handler.ts